<template>
  <div class="moves-table-container">
    <div class="moves-scroll">
      <table style="width: 100%">
        <!-- Pre game comment -->
        <tr v-if="preGameComment" key="preGameComment">
          <td colspan="3" class="move-cell move-comment-cell">
            <MoveComment :comment="preGameComment" />
          </td>
        </tr>

        <!-- if there is no moves show a message -->
        <tr v-if="!displayedRows.length">
          <td colspan="3" class="is-size-5 has-text-centered">No moves</td>
        </tr>

        <!-- List of moves -->
        <tbody>
          <div v-for="(row, index) in displayedRows" :key="`row-${index}`">
            <tr>
              <td style="width: 10%" class="move-cell move-number">
                <span>{{ row.variationDepth === 0 ? row.number : "" }}</span>
                <span
                  v-if="row.variationDepth > 0"
                  class="variation-depth"
                  :class="`depth-${row.variationDepth}`"
                >
                  (v{{ getVariationOrder(row)
                  }}{{
                    row.variationDepth > 1 ? "." + row.variationDepth : ""
                  }})
                </span>
              </td>
              <td style="width: 50%" class="move-cell">
                <div v-if="row.white">
                  <HalfMove
                    v-if="shouldShowMove(row.white)"
                    :shouldShowMoveNumber="row.variationDepth > 0"
                    v-bind="getHalfMoveProps(row.white)"
                    @moveSelected="onMoveClick(row.white)"
                    @editMove="openEditModal(row.white)"
                  />
                </div>
                <EmptyMove v-else-if="row.variationDepth === 0" />
              </td>
              <td style="width: 50%" class="move-cell">
                <HalfMove
                  v-if="shouldShowMove(row.black)"
                  :shouldShowMoveNumber="row.variationDepth > 0 && !row.white"
                  v-bind="getHalfMoveProps(row.black)"
                  @moveSelected="onMoveClick(row.black)"
                  @editMove="openEditModal(row.black)"
                />
              </td>
            </tr>
            <!-- Comment row -->
            <tr
              v-if="
                currentMove &&
                (currentMove.comment || currentMove.nag) &&
                (currentMove == row.white || currentMove == row.black)
              "
              :key="`comment-${index}`"
            >
              <td colspan="3" class="move-cell move-comment-cell">
                <MoveComment
                  :comment="currentMove.comment"
                  :nag="currentMove.nag"
                />
              </td>
            </tr>
          </div>
        </tbody>
      </table>
    </div>

    <MoveAnnotationEdit
      v-if="selectedMove"
      :isActive="isEditModalActive"
      :comment="selectedMove.comment"
      :nag="selectedMove.nag"
      @close="isEditModalActive = false"
      @save="handleAnnotationSave"
    />

    <div class="navigation-sticky">
      <!-- Game Result Display -->
      <div v-if="currentMoveResult" class="game-result-display">
        {{ currentMoveResult }}
      </div>

      <MoveNavigation
        :isAtStart="isAtStart"
        :isAtEnd="isAtEnd"
        :showVariations="showVariations"
        @previousMove="handlePreviousMove"
        @nextMove="handleNextMove"
        @goToStart="handleGoToStart"
        @goToEnd="handleGoToEnd"
        @previousVariation="handlePreviousVariation"
        @nextVariation="handleNextVariation"
        @toggleVariations="handleToggleVariations"
      />
    </div>
  </div>
</template>

<script>
import EmptyMove from "./EmptyMove.vue";
import HalfMove from "./HalfMove.vue";
import MoveAnnotationEdit from "./MoveAnnotationEdit.vue";
import MoveComment from "./MoveComment.vue";
import MoveNavigation from "./MoveNavigation.vue";

export default {
  name: "ScenarioMovesTable",

  components: {
    HalfMove,
    EmptyMove,
    MoveNavigation,
    MoveAnnotationEdit,
    MoveComment,
  },

  props: {
    pgnData: {
      type: Object,
      required: true,
    },

    currentMove: {
      type: Object,
      default: null,
    },

    isPresentationMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isEditModalActive: false,
      selectedMove: null,
      showVariations: true,
      isMounted: false,
    };
  },

  mounted() {
    this.isMounted = true;
  },

  beforeDestroy() {
    this.isMounted = false;
  },

  computed: {
    preGameComment() {
      return this.pgnData.preGameComment;
    },

    displayedRows() {
      if (!this.pgnData) return [];
      const rows =
        this.pgnData.getRows({ includeVariations: this.showVariations }) || [];

      // if presentation mode filter out rows that has no moves to show
      if (this.isPresentationMode) {
        return rows.filter(
          (row) =>
            this.shouldShowMove(row.white) || this.shouldShowMove(row.black)
        );
      }

      return rows;
    },

    currentLineMoveIds() {
      if (!this.currentMove) return new Set();

      const moveIds = new Set();

      // Add all previous moves
      let previousMove = this.currentMove;
      while (previousMove) {
        moveIds.add(previousMove.id);
        previousMove = previousMove.previousMove;
      }

      // Add all next moves
      let nextMove = this.currentMove.nextMove;
      while (nextMove) {
        moveIds.add(nextMove.id);
        nextMove = nextMove.nextMove;
      }

      return moveIds;
    },

    firstMove() {
      return this.pgnData?.rootMove;
    },

    isAtStart() {
      return !this.currentMove?.id;
    },

    isAtEnd() {
      return this.isAtStart ? !this.firstMove : !this.currentMove?.nextMove;
    },

    currentMoveResult() {
      if (!this.currentMove || !this.currentMove.resultString) {
        return null;
      }
      return this.currentMove.resultString;
    },
  },

  watch: {
    currentMove: {
      handler() {
        this.scrollToActiveMove();
      },
      immediate: false,
    },
  },

  methods: {
    isInCurrentLine(move) {
      if (!move || !this.currentMove) return false;
      return this.currentLineMoveIds.has(move.id);
    },

    shouldShowMove(move) {
      if (!move) return false;
      if (!this.isPresentationMode) return true;
      return (
        this.isInCurrentLine(move) &&
        !move.isInlineAndAfter(this.currentMove.id)
      );
    },

    isVariationOfCurrentMove(move) {
      if (!move || !this.currentMove) return false;
      if (move === this.currentMove) return false;

      return (
        move.parentMove === this.currentMove ||
        this.currentMove.parentMove === move ||
        (move.parentMove && move.parentMove === this.currentMove.parentMove)
      );
    },

    getHalfMoveProps(move) {
      return {
        moveNumber: move.number,
        san: move.mask || move.san,
        isMasked: !!move.mask,
        isBlack: move.color === "b",
        isActive: this.currentMove && this.currentMove.id === move.id,
        isInCurrentLine: this.isInCurrentLine(move),
        isVariationOfCurrentMove: this.isVariationOfCurrentMove(move),
        variationDepth: move.variationDepth,
        isVariationBegin: move.isVariationBegin,
        isVariationEnd: move.isVariationEnd,
        displayColor: move.displayColor,
        userScore: move.userScore,
        comment: move.comment,
        nag: move.nag,
      };
    },

    onMoveClick(move) {
      this.$emit("moveSelected", move);
    },

    handlePreviousMove() {
      this.$emit("moveSelected", this.currentMove?.previousMove || {});
    },

    handleNextMove() {
      if (!this.firstMove) return;
      if (this.isAtStart) {
        this.$emit("moveSelected", this.firstMove);
      } else if (this.currentMove.nextMove) {
        this.$emit("moveSelected", this.currentMove.nextMove);
      }
    },

    handleGoToStart() {
      // Go directly to the start position (before any moves)
      this.$emit("moveSelected", {});
    },

    handleGoToEnd() {
      if (!this.firstMove) return;

      let lastMove = this.currentMove?.id ? this.currentMove : this.firstMove;

      if (lastMove.isVariationEnd) {
        // If end of a variation line, go to main line
        lastMove = this.firstMove;
      }

      while (lastMove.nextMove) {
        lastMove = lastMove.nextMove;
      }

      if (lastMove) {
        this.$emit("moveSelected", lastMove);
      }
    },

    handlePreviousVariation() {
      if (!this.currentMove?.id) return;
      let move = this.currentMove;

      //If no child, go to the next variation
      if (!move.parentMove && !move.childMoves) {
        move = move.firstMoveInLine;
      }

      // Get all variations
      const variations = [];
      if (move.parentMove) {
        variations.push(move.parentMove);
        variations.push(...move.parentMove.childMoves);
      } else if (move.childMoves) {
        variations.push(move);
        variations.push(...move.childMoves);
      }

      const currentIndex = variations.indexOf(move);
      const previousMove =
        variations[currentIndex > 0 ? currentIndex - 1 : variations.length - 1];
      if (previousMove) {
        this.$emit("moveSelected", previousMove);
      }
    },

    handleNextVariation() {
      if (!this.currentMove?.id) return;
      let move = this.currentMove;

      //If no child, go to the next variation
      if (move.variationDepth && !move.parentMove && !move.childMoves) {
        move = move.firstMoveInLine;
      }

      // Get all variations
      const variations = [];
      if (move.parentMove) {
        variations.push(move.parentMove);
        variations.push(...move.parentMove.childMoves);
      } else if (move.childMoves) {
        variations.push(move);
        variations.push(...move.childMoves);
      }

      const currentIndex = variations.indexOf(move);
      const nextMove = variations[(currentIndex + 1) % variations.length];
      if (nextMove) {
        this.$emit("moveSelected", nextMove);
      }
    },

    openEditModal(move) {
      this.selectedMove = move;
      this.isEditModalActive = true;
    },

    handleAnnotationSave({ comment, nag }) {
      if (this.selectedMove) {
        this.selectedMove.updateNag(nag);
        this.selectedMove.updateComment(comment);
      }
      this.isEditModalActive = false;
    },

    handleToggleVariations() {
      this.showVariations = !this.showVariations;
      if (!this.showVariations && this.currentMove?.variationDepth > 0) {
        let mainLineMove = this.currentMove;
        while (mainLineMove?.variationDepth > 0) {
          mainLineMove = mainLineMove.firstMoveInLine.parentMove;
        }
        if (mainLineMove) {
          this.$emit("moveSelected", mainLineMove);
        }
      }
    },

    scrollToActiveMove() {
      if (!this.isMounted) return;

      this.$nextTick(() => {
        const activeMove = this.$el?.querySelector?.(".half-move.active");
        const container = this.$el?.querySelector?.(".moves-scroll");

        if (!activeMove || !container) return;

        const containerRect = container.getBoundingClientRect();
        const activeMoveRect = activeMove.getBoundingClientRect();
        const offsetHeight = 80;

        if (
          activeMoveRect.bottom > containerRect.bottom - offsetHeight ||
          activeMoveRect.top < containerRect.top + offsetHeight
        ) {
          const activeMoveTop = activeMove.offsetTop;
          const containerHeight = container.clientHeight;
          const scrollToPosition = activeMoveTop - containerHeight / 2;

          container.scrollTo({
            top: scrollToPosition,
            behavior: "smooth",
          });
        }
      });
    },

    getVariationOrder(row) {
      // Get the first move of the current variation line
      const move = row.white || row.black;
      if (!move) return 1;

      // If it's the first move in a variation, find its order in parent's childMoves
      if (move.isVariationBegin && move.parentMove) {
        const index = move.parentMove.childMoves.indexOf(move);
        return index + 1; // Add 1 to make it 1-based instead of 0-based
      }

      // If it's not the first move, traverse back to find the variation begin
      let currentMove = move;
      while (currentMove.previousMoveInline) {
        currentMove = currentMove.previousMoveInline;
      }

      // Now we have the first move of the variation
      if (currentMove.parentMove) {
        const index = currentMove.parentMove.childMoves.indexOf(currentMove);
        return index + 1;
      }

      return 1; // Default to 1 if we can't determine the order
    },
  },
};
</script>

<style scoped>
.moves-table-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
}

.moves-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

.move-cell {
  text-align: left;
  padding: 0.125rem;
  vertical-align: middle;
  height: 2.5rem;
  line-height: 1.2;
}

.move-number {
  min-width: 1.5rem;
  display: flex;
  align-items: center;
}

.move-comment-cell {
  padding: 0 !important;
  border-bottom: 1px solid #dbdbdb;
}

.navigation-sticky {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 0.2rem;
  border-top: 1px solid #eee;
  z-index: 1;
  border-radius: 0 0 6px 6px;
}

.variation-depth {
  font-size: 0.8em;
  opacity: 0.7;
  margin-left: 0.25rem;
}

.depth-1 {
  color: #555;
}
.depth-2 {
  color: #666;
}
.depth-3 {
  color: #777;
}

.game-result-display {
  text-align: center;
  font-weight: bold;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  background-color: #f5f5f5;
  border-radius: 4px;
  color: #363636;
}
</style>
